import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { formatUIDateString } from './components/Tools.ts';
import depositStyles from '../../stylesheets/management_site/deposit.module.scss';

const DepositDialog = forwardRef(
  (
    {
      bank_accounts = [],
      undeposited_payments = [],
      site_lookup = {},
      site_tenant_names = {},
    },
    ref
  ) => {
    const [selectedBank, setSelectedBank] = useState(
      bank_accounts[0]?.id || ''
    );
    const [showAllChecks, setShowAllChecks] = useState(false);
    const [selected, setSelected] = useState({});
    const [sortField, setSortField] = useState('date');
    const [sortAsc, setSortAsc] = useState(true);
    const [memo, setMemo] = useState('');
    const [depositDate, setDepositDate] = useState(
      new Date().toLocaleDateString()
    );
    const [selectAll, setSelectAll] = useState(false);

    const togglePayment = (id) => {
      setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSort = (field) => {
      if (sortField === field) {
        setSortAsc(!sortAsc);
      } else {
        setSortField(field);
        setSortAsc(true);
      }
    };

    const filteredPayments = undeposited_payments.filter(
      (p) => showAllChecks || p.operating_account_id == selectedBank
    );

    const computeSiteAndTenantInfo = (p) => {
      const siteStrings = Array.isArray(p.site_string)
        ? p.site_string
        : [p.site_string];
      let siteName = '';
      let primaryTenants = '';
      siteStrings.forEach((s) => {
        const info = site_lookup[s];
        if (info) {
          const name = info.number
            ? `${info.building_name} #${info.number}`
            : info.name || info.building_name || '';
          siteName = siteName ? `${siteName}, ${name}` : name;
          if (typeof s === 'string' && s.startsWith('Unit_') && p.is_tenant) {
            const unitKey = s.split('_')[1];
            const tenantInfo =
              site_tenant_names[unitKey] ||
              site_tenant_names[parseInt(unitKey, 10)];
            if (tenantInfo?.[0]) {
              const tname = tenantInfo[0].tenant_names;
              primaryTenants = primaryTenants
                ? `${primaryTenants}, ${tname}`
                : tname;
            }
          }
        }
      });
      return { siteName, primaryTenants };
    };

    const sortedPayments = [...filteredPayments].sort((a, b) => {
      const getVal = (p) => {
        let siteAndTenantInfo;
        if (['site', 'primaryTenants'].includes(sortField)) {
          siteAndTenantInfo = computeSiteAndTenantInfo(p);
        }
        switch (sortField) {
          case 'date':
            return new Date(p.time);
          case 'amount':
            return parseFloat(p.amount || 0);
          case 'alpha':
            return (p.entity_name || '').toLowerCase();
          case 'checkNo':
            return p.payment_method_attrs?.number || '';
          case 'memo':
            return p.payment_method_attrs?.memo || '';
          case 'type':
            return p.payment_method_attrs?.type || '';
          case 'site':
            return siteAndTenantInfo.siteName;
          case 'primaryTenants':
            return siteAndTenantInfo.primaryTenants;
          default:
            return p[sortField];
        }
      };
      const valA = getVal(a);
      const valB = getVal(b);
      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });

    const total = filteredPayments.reduce(
      (sum, p) => (selected[p.id] ? sum + parseFloat(p.amount || 0) : sum),
      0
    );

    const scrollRef = useRef(null);
    const rowVirtualizer = useVirtualizer({
      count: sortedPayments.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: useMemo(() => () => 50, []),
      overscan: 6,
      measureElement: (el) => el.getBoundingClientRect().height,
      getItemKey: (i) => sortedPayments[i]?.id,
    });

    useEffect(() => {
      const select = $('#bank_id');
      const handleChange = (e) => setSelectedBank(e.target.value);
      if (select.length) {
        select.select2();
        select.on('change', handleChange);
      }
      if (window.initReactDatepicker) {
        window.initReactDatepicker('#p2_deposit_time', (e) =>
          setDepositDate(e.target.value)
        );
      }
      return () => {
        if (select.length) {
          select.off('change', handleChange);
          select.select2('destroy');
        }
      };
    }, []);

    useEffect(() => {
      const allSelected =
        filteredPayments.length > 0 &&
        filteredPayments.every((p) => selected[p.id]);
      setSelectAll(allSelected);
    }, [filteredPayments, selected]);

    const getFormData = useCallback(() => {
      const items = {};
      let idx = 0;
      undeposited_payments.forEach((p) => {
        if (selected[p.id]) {
          items[idx] = {
            deposited_transaction_parent_id: p.id,
            deposited_transaction_parent_type: p.transaction_type_type,
            amount: p.amount,
          };
          idx += 1;
        }
      });
      return {
        p2_deposit: {
          time: depositDate,
          items_attributes: items,
        },
        bank_id: selectedBank,
        memo,
      };
    }, [selected, depositDate, selectedBank, memo, undeposited_payments]);

    useImperativeHandle(ref, () => ({ getFormData }), [getFormData, ref]);

    const toggleSelectAll = (checked) => {
      const newSelected = { ...selected };
      filteredPayments.forEach((p) => {
        newSelected[p.id] = checked;
      });
      setSelected(newSelected);
      setSelectAll(checked);
    };

    return (
      <aside
        id="mdc-dialog-newdeposit1"
        className="mdc-dialog mdc-dialog--open"
        role="alertdialog"
        aria-hidden="true"
        aria-labelledby="mdc-dialog-default-label"
        aria-describedby="mdc-dialog-default-description"
      >
        <div id="deposit-dialog-surface" className="mdc-dialog__surface">
          <header className="mdc-dialog__header pm-bg-color--blue1">
            <h2
              id="mdc-dialog-default-label"
              className="mdc-dialog__header__title"
            >
              New Deposit
            </h2>
          </header>
          <section
            id="bank-list"
            className="mdc-dialog__body mdc-dialog__body--scrollable"
          >
            {bank_accounts.length === 0 ? (
              <div id="deposit-unavailable-message">
                There are no banks accounts available to deposit to.
              </div>
            ) : undeposited_payments.length === 0 ? (
              <div id="deposit-unavailable-message">
                There are no received payments available for deposit.
              </div>
            ) : (
              <>
                <div className="pm-flex pm-flex-2">
                  <div className="pm-flex pm-flex-col">
                    <div className="pm-select">
                      <label>Bank Account</label>
                      <select
                        id="bank_id"
                        className="pm-default-select2-menu mdc-select pm-select-small"
                        value={selectedBank}
                        onChange={(e) => setSelectedBank(e.target.value)}
                      >
                        {bank_accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="pm-flex pm-flex-2">
                  <div className="pm-flex pm-flex-col">
                    <div className="mdc-form-field">
                      <div
                        id="mdc-js-checkbox"
                        className="mdc-checkbox"
                        data-mdc-auto-init="MDCCheckbox"
                      >
                        <input
                          type="checkbox"
                          id="show_all_checks"
                          className="mdc-checkbox__native-control"
                          checked={showAllChecks}
                          onChange={(e) => setShowAllChecks(e.target.checked)}
                        />
                        <div className="mdc-checkbox__background">
                          <svg
                            className="mdc-checkbox__checkmark"
                            viewBox="0 0 24 24"
                          >
                            <path
                              className="mdc-checkbox__checkmark-path"
                              fill="none"
                              stroke="white"
                              d="M1.73,12.91 8.1,19.28 22.79,4.59"
                            />
                          </svg>
                          <div className="mdc-checkbox__mixedmark"></div>
                        </div>
                      </div>
                      <label htmlFor="show_all_checks">Show all checks</label>
                    </div>
                  </div>
                </div>
                <div className="pm-flex pm-flex-3">
                  <div className="pm-flex pm-flex-col">
                    <div className="mdc-form-field mdc-form-field--align-end">
                      <div
                        className="mdc-text-field pm-margin-none"
                        data-mdc-auto-init="MDCTextField"
                      >
                        <input
                          type="text"
                          id="memo"
                          className="mdc-text-field__input"
                          value={memo}
                          onChange={(e) => setMemo(e.target.value)}
                        />
                        <label htmlFor="memo" className="mdc-floating-label">
                          Memo
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="pm-flex pm-flex-col">
                    <div className="mdc-form-field mdc-form-field--align-end">
                      <div
                        className="mdc-text-field pm-margin-none"
                        data-mdc-auto-init="MDCTextField"
                      >
                        <input
                          type="text"
                          id="p2_deposit_time"
                          className="mdc-text-field__input pm-datepicker-field"
                          value={depositDate}
                          onChange={(e) => setDepositDate(e.target.value)}
                        />
                        <label
                          htmlFor="p2_deposit_time"
                          className="mdc-floating-label"
                        >
                          Deposit Date
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`pm-flex pm-flex-2 ${depositStyles.tableContainer}`}
                  ref={scrollRef}
                >
                  <table
                    className={`pm-modal-table pm-big-table pm-margin-bottom-16 ${depositStyles.paymentsTable}`}
                    id="payments_for_deposit"
                  >
                    <thead>
                      <tr>
                        <th>
                          <div className="mdc-form-field">
                            <div
                              id="mdc-js-checkbox"
                              className="mdc-checkbox"
                              data-mdc-auto-init="MDCCheckbox"
                            >
                              <input
                                type="checkbox"
                                id="deposit_all"
                                className="mdc-checkbox__native-control"
                                checked={selectAll}
                                onChange={(e) =>
                                  toggleSelectAll(e.target.checked)
                                }
                              />
                              <div className="mdc-checkbox__background">
                                <svg
                                  className="mdc-checkbox__checkmark"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    className="mdc-checkbox__checkmark-path"
                                    fill="none"
                                    stroke="white"
                                    d="M1.73,12.91 8.1,19.28 22.79,4.59"
                                  />
                                </svg>
                                <div className="mdc-checkbox__mixedmark"></div>
                              </div>
                            </div>
                          </div>
                        </th>
                        <th
                          className={`sortable-header-field deposit-date-header ${depositStyles.sortableHeader}`}
                          data-sort-field="date"
                          onClick={() => handleSort('date')}
                        >
                          Date
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="alpha"
                          onClick={() => handleSort('alpha')}
                        >
                          Received From
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="type"
                          onClick={() => handleSort('type')}
                        >
                          Type
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="checkNo"
                          onClick={() => handleSort('checkNo')}
                        >
                          Check No.
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="memo"
                          onClick={() => handleSort('memo')}
                        >
                          Memo
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="site"
                          onClick={() => handleSort('site')}
                        >
                          Site
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="primaryTenants"
                          onClick={() => handleSort('primaryTenants')}
                        >
                          Primary Tenants
                        </th>
                        <th
                          className={`sortable-header-field ${depositStyles.sortableHeader}`}
                          data-sort-field="amount"
                          onClick={() => handleSort('amount')}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody
                      className={depositStyles.virtualizedTableBody}
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const p = sortedPayments[virtualRow.index];
                        if (!p) return null;
                        const { siteName, primaryTenants } =
                          computeSiteAndTenantInfo(p);
                        return (
                          <tr
                            key={p.id}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            className={`deposit_row ${depositStyles.virtualizedRow}`}
                            style={{
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            data-date={formatUIDateString(p.time)}
                            data-received-from={p.entity_name}
                            data-type={p.payment_method_attrs?.type}
                            data-check-no={p.payment_method_attrs?.number}
                            data-memo={p.payment_method_attrs?.memo}
                            data-site={siteName}
                            data-primary-tenants={primaryTenants}
                            data-amount={p.amount}
                          >
                            <td>
                              <div
                                id="mdc-js-checkbox"
                                className="mdc-checkbox"
                                data-mdc-auto-init="MDCCheckbox"
                              >
                                <input
                                  type="checkbox"
                                  className="mdc-checkbox__native-control deposit_payment_checkbox"
                                  checked={!!selected[p.id]}
                                  onChange={() => togglePayment(p.id)}
                                />
                                <div className="mdc-checkbox__background">
                                  <svg
                                    className="mdc-checkbox__checkmark"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      className="mdc-checkbox__checkmark-path"
                                      fill="none"
                                      stroke="white"
                                      d="M1.73,12.91 8.1,19.28 22.79,4.59"
                                    />
                                  </svg>
                                  <div className="mdc-checkbox__mixedmark"></div>
                                </div>
                              </div>
                            </td>
                            <td>{formatUIDateString(p.time)}</td>
                            <td>{p.entity_name}</td>
                            <td>{p.payment_method_attrs?.type}</td>
                            <td>{p.payment_method_attrs?.number}</td>
                            <td>{p.payment_method_attrs?.memo}</td>
                            <td>{siteName}</td>
                            <td>{primaryTenants}</td>
                            <td>{p.amount}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mdc-form-field mdc-form-field--align-end">
                  <div
                    className="mdc-text-field pm-margin-none"
                    data-mdc-auto-init="MDCTextField"
                  >
                    <input
                      id="total_amount"
                      type="text"
                      disabled
                      value={total.toFixed(2)}
                      className={`mdc-text-field__input pm-text-color--blue1 ${depositStyles.totalAmountInput}`}
                    />
                    <label className="mdc-floating-label">Total amount</label>
                  </div>
                </div>
              </>
            )}
          </section>
          <footer className="mdc-dialog__footer">
            <button
              type="button"
              className="mdc-button pm-text-color--grey5 mdc-dialog__footer__button mdc-dialog__footer__button--cancelPM"
            >
              Cancel
            </button>
            <button
              type="button"
              className="mdc-button pm-text-color--blue1 mdc-dialog__footer__button mdc-dialog__footer__button--acceptPM"
            >
              Save
            </button>
          </footer>
        </div>
        <div className="mdc-dialog__backdrop"></div>
      </aside>
    );
  }
);

export default DepositDialog;
